import { Pagination, Table, Tag } from "rsuite";
import { useEffect, useRef, useState, useMemo } from "react";
import { Card } from "./card.component";
import { TextCell } from "./tableItem.component";
import { useAppDispatch, useAppSelector } from "../core/redux/ReduxStore";
import upSolidIcon from "../assets/img/header-chevron-up-solid.svg";
import downSolidIcon from "../assets/img/header-chevron-up-solid.svg";
import {
  ApiSchemaPaginatedPatientCountByInsuranceType,
  ApiSchemaPatientCountByInsuranceType,
} from "../models/api/apiSuperAdminDashboard";
import { ActionsInsuranceTableInReportingTab } from "../core/redux/slices/sliceInsuranceTableOnReportingTab";
import {
  useTablePaginationSearchFilter,
  IPaginationSearchFilter,
} from "../hooks";
import { AppRouteApi } from "../config/appRoute";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaPatientCountByInsuranceType & {
  sl?: number;
};

export const InsuranceListOnReportingTab = () => {
  const triggerRef = useRef<any>();
  const [tableData, setTableData] = useState<TableData[]>([]);

  const dispatch = useAppDispatch();
  const { pageNumber, pageSize, searchKey, nextPageToken, isLoading } =
    useAppSelector((state) => state.insuranceTableInReportingTab);
  const customParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      SearchKey: searchKey,
      PageNumber: pageNumber,
      NextPageToken: nextPageToken,
    };
  }, [searchKey, pageNumber, nextPageToken]);
  const { apiData } = useTablePaginationSearchFilter<
    ApiSchemaPaginatedPatientCountByInsuranceType,
    typeof ActionsInsuranceTableInReportingTab
  >(ActionsInsuranceTableInReportingTab, {
    customParams,
    apiRoute: AppRouteApi.Reporting.PaginatedPatientCountByInsuranceType(),
  });

  useEffect(
    () =>
      setTableData(
        apiData?.list?.map((insuranceType, index) => ({
          sl: (pageNumber - 1) * pageSize + index + 1,
          ...insuranceType,
        })) ?? []
      ),
    [apiData, pageNumber, pageSize]
  );

  useEffect(() => {
    return () => {
      dispatch(ActionsInsuranceTableInReportingTab.resetData());
    };
  }, [dispatch]);

  return (
    <Card
      header={
        <div className="row align-items-center  justify-content-between">
          <div className="col-md-8 col-12 content-box-title">
            <div>
              <p className="text-capitalize my-1 text-green-200">
                Patient Count By Insurance
              </p>
            </div>
            <div className="collapse-icon"></div>
          </div>
          <a
            className="btn  box-collapse-btn me-1"
            data-bs-toggle="collapse"
            href="#report-insurance-table"
            role="button"
            aria-expanded="false"
            aria-controls="report-insurance-table"
          >
            <img
              src={upSolidIcon}
              alt="up"
              className="img-fluid svg-btn collapse-close"
            />
            <img
              src={downSolidIcon}
              alt="down"
              className="img-fluid svg-btn collapse-open"
            />
          </a>
        </div>
      }
    >
      <div
        className="collapse show clearfix position-relative"
        id="report-insurance-table"
      >
        <Table
          autoHeight
          data={tableData.filter(
            (val, idx) =>
              idx >= (pageNumber - 1) * pageSize && idx < pageNumber * pageSize
          )}
          loading={isLoading}
          onScroll={() => triggerRef.current?.close()}
          renderEmpty={() => (
            <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
              <h3 className="fw-light">No Insurance Available</h3>
            </div>
          )}
        >
          <Column align="center" width={60}>
            <HeaderCell className="fw-bold text-black">
              <span title="S/L">S/L</span>
            </HeaderCell>
            <TextCell component="Insurance" dataKey="sl" />
          </Column>

          <Column width={350}>
            <HeaderCell className="fw-bold text-black">
              <span title="Name">Name</span>
            </HeaderCell>
            <TextCell component="Insurance" dataKey="insuranceTypeName" />
          </Column>
          <Column align="center">
            <HeaderCell className="fw-bold text-black">
              <span title="Code">Patients</span>
            </HeaderCell>
            <Cell>
              {(rowData: TableData) => (
                <Tag color="violet">{rowData.patientCount}</Tag>
              )}
            </Cell>
          </Column>
        </Table>

        {apiData?.list && (
          <div className="d-flex justify-content-end mt-3">
            <Pagination
              total={apiData?.list.length}
              pages={Math.ceil(apiData.totalCount / pageSize)}
              maxButtons={6}
              next={true}
              prev={true}
              activePage={pageNumber}
              onSelect={(page) =>
                dispatch(ActionsInsuranceTableInReportingTab.goToPage(page))
              }
            />
          </div>
        )}
      </div>
    </Card>
  );
};
